import { Fragment, useMemo } from 'react';
import { Link as RRLink } from 'react-router-dom';
// Models
import { IUsage } from 'app/store/BillingUsage/BillingUsage.models';
// Mui
import { Link, Paper, Table, TableBody, TableCell, TableContainer, TableFooter, TableHead, TableRow, Theme } from '@mui/material';
// Icons
import { OpenInNew as OpenInNewIcon } from '@mui/icons-material';
// Utils
import { toNumberWithCommas } from 'app/utilities/Utilities';
// 
import InvoiceNumberChips from './InvoiceNumberChips';
// i18next
import { useTranslation } from 'react-i18next';
import useGroupByCaseService from './useGroupByCaseService';

type Props = {
  data: IUsage[];
}

export default function UsageByCaseService({ data }: Props) {
  const { t } = useTranslation();

  const groupByCaseService = useGroupByCaseService(data);

  const totalAmount = useMemo(() => {
    return groupByCaseService.reduce((acc, caseItem) => {
      const serviceTotal = caseItem.services.reduce((caseSum, service) => caseSum + service.amount, 0);
      return acc + serviceTotal;
    }, 0);
  }, [groupByCaseService]);

  return (
    <TableContainer component={Paper} variant="outlined">
      <Table stickyHeader>
        <TableHead>
          <TableRow>
            <TableCell>{t('labels.caseName')}</TableCell>
            <TableCell sx={{ width: '33.33%' }}>{t('labels.invoices')}</TableCell>
            <TableCell>{t('labels.serviceName')}</TableCell>
            <TableCell>{t('labels.qty')}</TableCell>
            <TableCell>{t('labels.total')}</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {groupByCaseService.map((group) => (
            <Fragment key={`caseItem-${group.id}`}>
              <TableRow>
                <TableCell rowSpan={group.services.length + 1}>
                  <Link
                    sx={{ display: 'flex', alignItems: 'center', gap: 2 }}
                    component={RRLink}
                    target="_blank"
                    to={`/admin/cases/${group.id}/documents`}
                    underline="none"
                  >
                    {group.name}
                    <OpenInNewIcon fontSize="small" />
                  </Link>
                </TableCell>
                <TableCell rowSpan={group.services.length + 1}>
                  <InvoiceNumberChips invoices={group.invoices || []} />
                </TableCell>
              </TableRow>
              {group.services.map((service) => (
                <TableRow key={`${group.id}-serviceItem-${service.id}`}>
                  <TableCell>{service.name}</TableCell>
                  <TableCell>{service.quantity}</TableCell>
                  <TableCell>{`$${toNumberWithCommas(service.amount)}`}</TableCell>
                </TableRow>
              ))}
            </Fragment>
          ))}
        </TableBody>
        <TableFooter sx={{ position: 'sticky', bottom: 0, backgroundColor: 'white', zIndex: 1 }}>
          <TableRow sx={{
            bgcolor: (theme:Theme) => theme.palette.grey[50],
            '& > td': {
              borderBottom: 0
            }
          }}>
            <TableCell sx={{ fontWeight: 700, fontSize: '0.875rem' }} colSpan={4}>{t('labels.total')}</TableCell>
            <TableCell sx={{ fontWeight: 700, fontSize: '0.875rem' }}>{`$${toNumberWithCommas(totalAmount)}`}</TableCell>
          </TableRow>
        </TableFooter>
      </Table>
    </TableContainer>
  )
};
